/**
 * Maps between visual automation editor nodes (canvas) and persisted / runtime
 * AutomationStep[] shape expected by automationExecutionEngine (action + send_email, etc.).
 */

import type { AutomationStep } from './automationService';

export interface CanvasStepLike {
  id: string;
  type: string;
  title?: string;
  description?: string;
  config?: Record<string, unknown>;
  position?: { x: number; y: number };
  connections?: string[];
  enabled?: boolean;
}

type CanvasStepFields = Omit<CanvasStepLike, 'type'>;

const HOURS_PER_UNIT: Record<string, number> = {
  minutes: 1 / 60,
  hours: 1,
  days: 24,
  weeks: 24 * 7,
};

function editorDelayToWaitHours(amount: number, unit: string): number {
  const mult = HOURS_PER_UNIT[unit] ?? 1;
  return Math.max(0, Number(amount) || 0) * mult;
}

function waitHoursToEditorFields(waitValue: number, editorMeta?: { amount?: number; unit?: string; onlySelectedDays?: boolean }) {
  if (editorMeta && typeof editorMeta.amount === 'number' && editorMeta.unit) {
    return {
      amount: editorMeta.amount,
      unit: editorMeta.unit as 'minutes' | 'hours' | 'days' | 'weeks',
      onlySelectedDays: Boolean(editorMeta.onlySelectedDays),
    };
  }
  const wv = Math.max(0, Number(waitValue) || 0);
  return { amount: wv || 1, unit: 'hours' as const, onlySelectedDays: false };
}

function reverseBirthdayTriggerConfig(cfg: Record<string, unknown>): Record<string, unknown> {
  if (
    cfg &&
    cfg.type === 'anniversary' &&
    (cfg as { config?: { anniversaryField?: string } }).config?.anniversaryField === 'birthday'
  ) {
    const inner = (cfg as { config?: Record<string, unknown> }).config || {};
    return {
      ...cfg,
      type: 'birthday',
      event: 'birthday',
      config: {
        ...inner,
        daysBefore: inner.daysBefore ?? 0,
        daysAfter: inner.daysAfter ?? 0,
      },
    };
  }
  return cfg;
}

/**
 * Persisted / engine steps → canvas WorkflowElement-like rows (x/y + canvas type names).
 */
export function automationStepsToCanvasSteps(steps: unknown[]): CanvasStepLike[] {
  if (!Array.isArray(steps)) return [];

  return steps.map((raw, index) => {
    const step = raw as Record<string, unknown>;
    const id = String(step.id ?? `step-${index}`);
    const pos = (step.position as { x?: number; y?: number }) || { x: 100, y: 50 + index * 150 };
    const base: CanvasStepFields = {
      id,
      title: String(step.title ?? step.type ?? 'Step'),
      description: typeof step.description === 'string' ? step.description : '',
      position: { x: Number(pos.x ?? 100), y: Number(pos.y ?? 50 + index * 150) },
      connections: Array.isArray(step.connections) ? (step.connections as string[]) : [],
      enabled: step.enabled !== false,
      config: {},
    };

    const t = String(step.type ?? '');

    if (t === 'trigger') {
      let cfg = { ...(typeof step.config === 'object' && step.config ? (step.config as object) : {}) } as Record<string, unknown>;
      cfg = reverseBirthdayTriggerConfig(cfg);
      return { ...base, type: 'trigger', config: cfg };
    }

    if (t === 'split') {
      return { ...base, type: 'split', config: (step.config as Record<string, unknown>) || {} };
    }

    if (t === 'exit') {
      return { ...base, type: 'exit', config: (step.config as Record<string, unknown>) || {} };
    }

    if (t === 'delay') {
      const cfg = (step.config as Record<string, unknown>) || {};
      const meta = cfg.editorMeta as { amount?: number; unit?: string; onlySelectedDays?: boolean } | undefined;
      const { amount, unit, onlySelectedDays } = waitHoursToEditorFields(Number(cfg.waitValue ?? 0), meta);
      return {
        ...base,
        type: 'delay',
        config: { amount, unit, onlySelectedDays },
      };
    }

    if (t === 'action') {
      const ac = (step.config as Record<string, unknown>) || {};
      const actionType = String(ac.type ?? '');
      const inner = (typeof ac.config === 'object' && ac.config ? ac.config : {}) as Record<string, unknown>;

      if (actionType === 'send_email') {
        return { ...base, type: 'email', config: inner };
      }
      if (actionType === 'send_sms') {
        return { ...base, type: 'sms', config: inner };
      }
      if (actionType === 'send_push') {
        return { ...base, type: 'push', config: inner };
      }
      if (actionType === 'add_tag') {
        return { ...base, type: 'tag', config: { ...inner, _tagAction: 'add' } };
      }
      if (actionType === 'remove_tag') {
        return { ...base, type: 'tag', config: { ...inner, _tagAction: 'remove' } };
      }
      if (actionType === 'ab_test') {
        const c = inner;
        const vars = Array.isArray(c.abVariants) ? (c.abVariants as Array<{ name?: string; percentage?: number }>) : [];
        const a = vars[0];
        const b = vars[1];
        return {
          ...base,
          type: 'ab_test',
          config: {
            trafficA: Number(a?.percentage ?? 50),
            trafficB: Number(b?.percentage ?? 50),
            goal: String(c.abGoal ?? 'open_rate'),
            windowHours: Number(c.abWinnerDelay ?? 24),
            autoWinner: c.abAutoSelectWinner !== false,
          },
        };
      }
    }

    // Legacy documents written directly from the canvas (pre-normalization)
    if (t === 'email' || t === 'sms' || t === 'push' || t === 'tag' || t === 'ab_test') {
      return { ...base, type: t, config: (step.config as Record<string, unknown>) || {} };
    }

    // Unknown — surface as email shell so user can fix in editor
    return { ...base, type: 'email', config: (step.config as Record<string, unknown>) || {} };
  });
}

/**
 * Canvas / editor elements → AutomationStep[] for Firestore + execution engine.
 */
export function canvasStepsToAutomationSteps(elements: CanvasStepLike[]): AutomationStep[] {
  return elements.map((el) => {
    const base = {
      id: el.id,
      title: el.title || el.type,
      description: el.description || '',
      position: el.position || { x: 100, y: 50 },
      connections: Array.isArray(el.connections) ? el.connections : [],
      enabled: el.enabled !== false,
    };

    const cfg = { ...(el.config || {}) } as Record<string, unknown>;

    switch (el.type) {
      case 'trigger':
        return { ...base, type: 'trigger', config: cfg as unknown as AutomationStep['config'] };
      case 'split':
        return { ...base, type: 'split', config: cfg as unknown as AutomationStep['config'] };
      case 'exit':
        return { ...base, type: 'exit', config: cfg as unknown as AutomationStep['config'] };
      case 'delay': {
        const amount = Number(cfg.amount ?? 1);
        const unit = String(cfg.unit || 'hours');
        const waitValue = editorDelayToWaitHours(amount, unit);
        return {
          ...base,
          type: 'delay',
          config: {
            waitType: 'fixed' as const,
            waitValue,
            editorMeta: {
              amount,
              unit,
              onlySelectedDays: Boolean(cfg.onlySelectedDays),
            },
          } as unknown as AutomationStep['config'],
        };
      }
      case 'email':
        return {
          ...base,
          type: 'action',
          config: {
            id: el.id,
            type: 'send_email',
            config: cfg,
          } as unknown as AutomationStep['config'],
        };
      case 'sms':
        return {
          ...base,
          type: 'action',
          config: {
            id: el.id,
            type: 'send_sms',
            config: cfg,
          } as unknown as AutomationStep['config'],
        };
      case 'push':
        return {
          ...base,
          type: 'action',
          config: {
            id: el.id,
            type: 'send_push',
            config: cfg,
          } as unknown as AutomationStep['config'],
        };
      case 'tag': {
        const tagAction = String(cfg._tagAction || 'add');
        const rest = { ...cfg };
        delete rest._tagAction;
        if (tagAction === 'remove') {
          return {
            ...base,
            type: 'action',
            config: {
              id: el.id,
              type: 'remove_tag',
              config: rest,
            } as unknown as AutomationStep['config'],
          };
        }
        return {
          ...base,
          type: 'action',
          config: {
            id: el.id,
            type: 'add_tag',
            config: rest,
          } as unknown as AutomationStep['config'],
        };
      }
      case 'ab_test': {
        const yesId = Array.isArray(el.connections) ? el.connections[0] : undefined;
        const noId = Array.isArray(el.connections) ? el.connections[1] : undefined;
        const ta = Math.min(100, Math.max(0, Number(cfg.trafficA ?? 50)));
        const tb = Math.min(100, Math.max(0, Number(cfg.trafficB ?? 50)));
        return {
          ...base,
          type: 'action',
          config: {
            id: el.id,
            type: 'ab_test',
            config: {
              abVariants: [
                { name: 'A', percentage: ta || 50, nextStepId: yesId },
                { name: 'B', percentage: tb || 50, nextStepId: noId },
              ],
              abGoal: cfg.goal || 'open_rate',
              abWinnerDelay: Number(cfg.windowHours ?? 24),
              abAutoSelectWinner: cfg.autoWinner !== false,
            },
          } as unknown as AutomationStep['config'],
        };
      }
      case 'action':
      case 'condition': {
        const persistedConfig: unknown = cfg;
        return {
          ...base,
          type: el.type as AutomationStep['type'],
          config: persistedConfig as AutomationStep['config'],
        };
      }
      default:
        return {
          ...base,
          type: 'action',
          config: { id: el.id, type: 'send_email', config: {} } as unknown as AutomationStep['config'],
        };
    }
  });
}
